import React from 'react';
import { Box } from '@mui/material';
import { logger } from '../utils/logger';
import { t } from '../utils/localization/localization';
import { ErrorState } from './common';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0
    };
  }

  static getDerivedStateFromError(_error) {
    // Update state so the next render shows error state
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    logger.error('ErrorBoundary', 'React component error caught', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      errorBoundary: this.props.name || 'Unknown'
    });

    // Update state with error details
    this.setState(prevState => ({
      error,
      errorInfo,
      errorCount: prevState.errorCount + 1
    }));
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Use standardized ErrorState component
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          p={3}
        >
          <ErrorState
            error={this.state.error}
            title={this.state.errorCount > 2 ? 'Critical Error' : 'Something went wrong'}
            message={this.state.errorCount > 2
              ? 'This error has occurred multiple times. Please contact support if the problem persists.'
              : 'An unexpected error occurred. Please try refreshing the page.'}
            context={`React Error Boundary (${this.props.name || 'Unknown'})`}
            onRetry={this.handleReset}
            retryText={t('common.confirm', 'Try Again')}
            severity={this.state.errorCount > 2 ? 'error' : 'warning'}
            additionalInfo={process.env.NODE_ENV === 'development' ? this.state.errorInfo?.componentStack : null}
            component={`ErrorBoundary-${this.props.name || 'Global'}`}
            fullHeight={true}
          />
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
